/**
 * `ChatConversation` — the transcript surface. Pins the viewport to the
 * latest message (via the `Conversation` AI-element which wraps
 * `use-stick-to-bottom`), shows the empty state when there are no
 * messages, and renders a `ThinkingMessage` between turns while we
 * wait for the next assistant chunk.
 *
 * The transcript itself is a flat map over `messages`, dispatching to
 * `HumanMessage` / `AIMessage`. Per-part rendering and tool/reasoning
 * dispatch lives in `AIMessage`; this file just owns layout + the
 * "between turns" affordance.
 */
import type { UIMessage } from "ai";
import { Conversation, ConversationContent, ConversationScrollButton } from "@flint/ui";
import { AIMessage } from "../../components/ai-messages/ai-message.tsx";
import { HumanMessage } from "../../components/ai-messages/human-message.tsx";
import { ChatEmptyState } from "./chat-empty-state.tsx";

interface ChatConversationProps {
  messages: UIMessage[];
  isLoading: boolean;
  loadingThread: boolean;
  /** Called when the user picks a suggestion from the empty state. */
  onSelectSuggestion: (prompt: string) => void;
}

/**
 * Conversation owns the scrollable region: `flex-1 min-h-0` so the
 * composer below claims its space first and we don't overflow the
 * parent column. The StickToBottom primitive inside handles auto-pin
 * to the latest message and surfaces a scroll-to-bottom button when
 * the user scrolls up.
 */
export function ChatConversation({
  messages,
  isLoading,
  loadingThread,
  onSelectSuggestion,
}: ChatConversationProps) {
  return (
    <Conversation className="min-h-0 flex-1">
      <ConversationContent className="!gap-0 px-0 pb-3 pt-4">
        {loadingThread ? (
          <Centered>
            <p className="text-sm text-muted">Loading chat…</p>
          </Centered>
        ) : messages.length === 0 ? (
          // Empty state owns its own column width so the suggestion
          // cards fit a tighter 2-column grid than the transcript's
          // 4xl column — `Centered` would overstretch them.
          <ChatEmptyState onSelect={onSelectSuggestion} />
        ) : (
          <>
            {messages.map((message) => (
              <Centered key={message.id}>
                {message.role === "user" ? (
                  <HumanMessage message={message} />
                ) : (
                  <AIMessage
                    message={message}
                    isLoading={isLoading && isLastAssistant(messages, message)}
                  />
                )}
              </Centered>
            ))}

          </>
        )}
      </ConversationContent>
      <ConversationScrollButton />
    </Conversation>
  );
}

/**
 * Single source of truth for transcript column width. Taqat-style:
 * each message row owns its own bottom margin (`mb-6` between turns)
 * so adjacent messages can sit tight or spaced without the parent
 * `ConversationContent` imposing a uniform `gap`.
 *
 * `min-w-0` paired with the message-level `min-w-0` is what keeps
 * intrinsically wide content (markdown tables, long `pre` blocks)
 * from pushing the column past its `max-w-3xl` / `xl:max-w-4xl`
 * constraints. Without it those children inherit `min-width: auto`
 * and grow the column past the workspace's right edge.
 */
function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto mb-6 w-full min-w-0 max-w-full px-3 lg:max-w-3xl lg:px-4 xl:max-w-4xl">
      {children}
    </div>
  );
}

function isLastAssistant(messages: UIMessage[], target: UIMessage): boolean {
  for (let index = messages.length - 1; index >= 0; index--) {
    const candidate = messages[index];
    if (!candidate || candidate.role !== "assistant") continue;
    return candidate.id === target.id;
  }
  return false;
}
